import React, { useState, useEffect, useMemo } from 'react';
import { pageTrackingService } from '../../../services/pageTracking';
import { useParams, Link } from 'react-router';
import { observer } from 'mobx-react';
import styles from '../CategoryPageRedesign/CategoryPageRedesign.module.scss';
import Header from '../components/Header/Header';
import ScrollToTop from '../components/ScrollToTop/ScrollToTop';
import Footer from '../components/Footer/Footer';
import StoresAPI from '@api/stores-api';
import Product from '@models/product';
import { generateProductDetailsUrl } from '@utils/urlUtils';
import {
  formatDiscountPercentage,
  truncateDiscountPercent,
} from '@utils/formatDiscountPercentage';
import { cartStore, authStore, wishlistStore } from '@stores/main';
import { useToast } from '../../../contexts/ToastContext';
import { useBreadcrumbNavigation } from '@utils/breadcrumbNavigation';

const CollectionExpandPage: React.FC = observer(() => {
  const { feedId } = useParams<{ feedId: string }>();
  const { showToast } = useToast();
  const { goToLogin } = useBreadcrumbNavigation();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'popular' | 'price-low' | 'price-high' | 'newest'>('popular');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());
  const [addingToCart, setAddingToCart] = useState<Set<string>>(new Set());
  const [optimisticCartIds, setOptimisticCartIds] = useState<Set<string>>(new Set());
  const [updatingWishlist, setUpdatingWishlist] = useState<Set<string>>(new Set());
  const [collectionTitle, setCollectionTitle] = useState<string | null>(null);
  const storesAPI = useMemo(() => new StoresAPI(), []);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  useEffect(() => {
    let cancelled = false;
    const resolveTitle = async () => {
      if (!feedId) {
        setCollectionTitle(null);
        return;
      }
      try {
        const feeds = await storesAPI.getFeedList();
        if (cancelled) return;
        const feed = feeds.find((f) => String(f.id) === String(feedId));
        setCollectionTitle(feed?.title ?? null);
      } catch {
        if (!cancelled) setCollectionTitle(null);
      }
    };
    resolveTitle();
    return () => {
      cancelled = true;
    };
  }, [feedId, storesAPI]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'dark');
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchProducts = async () => {
      if (!feedId) {
        setLoading(false);
        setError('Invalid collection');
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const items = await storesAPI.getFeedExpand(feedId);
        if (cancelled) return;
        setProducts(items);
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching collection:', err);
        setError('Failed to load collection. Please try again.');
        setProducts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchProducts();
    return () => {
      cancelled = true;
    };
  }, [feedId, storesAPI]);

  const sortedProducts = useMemo(() => {
    const sorted = [...products];
    switch (sortBy) {
      case 'price-low':
        return sorted.sort((a, b) => a.buyPrice - b.buyPrice);
      case 'price-high':
        return sorted.sort((a, b) => b.buyPrice - a.buyPrice);
      case 'newest':
        return sorted.sort((a, b) => {
          const discountA = a.discountPercentage || 0;
          const discountB = b.discountPercentage || 0;
          if (discountB !== discountA) return discountB - discountA;
          if (a.hotOffer && !b.hotOffer) return -1;
          if (!a.hotOffer && b.hotOffer) return 1;
          return 0;
        });
      default:
        return sorted;
    }
  }, [products, sortBy]);

  const convertProductToCardFormat = (product: Product) => {
    const productUrl = product.id
      ? generateProductDetailsUrl({
          id: product.id,
          title: product.title || product.extendedTitle || 'Product',
        })
      : '#';
    const hasPriceDrop = product.displayPrice > product.buyPrice && product.displayPrice > 0;
    const computedDiscount = hasPriceDrop
      ? truncateDiscountPercent(
          ((product.displayPrice - product.buyPrice) / product.displayPrice) * 100
        )
      : 0;
    const discount =
      product.discountPercentage != null && product.discountPercentage > 0
        ? product.discountPercentage
        : computedDiscount > 0
          ? computedDiscount
          : undefined;
    return {
      id: product.id,
      name: product.title || product.extendedTitle || 'Product',
      price: product.buyPriceString || `₹${product.buyPrice}`,
      originalPrice:
        product.displayPriceString && product.displayPrice !== product.buyPrice
          ? product.displayPriceString
          : undefined,
      discount,
      gradient: product.background || 'linear-gradient(135deg, #42A5F5, #1E88E5)',
      imageUrl: product.poster || undefined,
      icon: 'fas fa-gamepad',
      tag: product.hotOffer ? 'Hot' : product.buttonDiscountText || undefined,
      link: productUrl,
    };
  };

  const handleImageError = (productId: string) => {
    setImageErrors((prev) => new Set(prev).add(productId));
  };

  const productPlaceholderUrl = `${process.env.PUBLIC_URL || ''}/images/product-placeholder.svg`;

  const isInWishlist = (productId?: string) =>
    !!productId && wishlistStore.wishlist.some((item) => item.id === productId);

  const getCartQuantity = (productId?: string) => {
    if (!productId) return 0;
    const id = String(productId);
    const cartItem = cartStore.items.find((item) => String(item.id) === id);
    const fromStore = Math.max(0, cartItem?.isInCartQuantity ?? cartItem?.quantity ?? 0);
    if (fromStore > 0) return fromStore;
    return optimisticCartIds.has(id) ? 1 : 0;
  };

  const isInCart = (product: Product) => getCartQuantity(product.id) > 0;

  const handleWishlistToggle = async (e: React.MouseEvent, product: Product) => {
    e.preventDefault();
    e.stopPropagation();
    if (!product.id) return;
    if (!authStore.isAuthenticated) {
      goToLogin();
      return;
    }
    setUpdatingWishlist((prev) => new Set(prev).add(product.id));
    try {
      const inWishlist = wishlistStore.wishlist.some((item) => item.id === product.id);
      if (inWishlist) {
        wishlistStore.removeProductFromWishlist(product);
        showToast('Removed from wishlist', 'info', 2500);
      } else {
        await wishlistStore.addProductToWishlist(product);
      }
    } catch (error) {
      console.error("Error updating wishlist:", error);
    } finally {
      setUpdatingWishlist((prev) => {
        const next = new Set(prev);
        next.delete(product.id);
        return next;
      });
    }
  };

  const handleAddToCart = async (product: Product, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!authStore.isAuthenticated) {
      goToLogin();
      return;
    }
    if (!product.id) return;
    const productIdStr = String(product.id);
    setAddingToCart((prev) => new Set(prev).add(product.id));
    setOptimisticCartIds((prev) => new Set(prev).add(productIdStr));
    try {
      const res = await cartStore.addItemToCart(product.id);
      if (!res) {
        return;
      }
      pageTrackingService.trackProductAddToCart(String(product.id), product.title || '', 'collection');
    } catch (error) {
      console.error("Error adding to cart:", error);
    } finally {
      setAddingToCart((prev) => {
        const next = new Set(prev);
        next.delete(product.id);
        return next;
      });
      setOptimisticCartIds((prev) => {
        const next = new Set(prev);
        next.delete(productIdStr);
        return next;
      });
    }
  };

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSortBy(e.target.value as 'popular' | 'price-low' | 'price-high' | 'newest');
  };

  const handleRetry = () => {
    if (feedId) {
      setError(null);
      setLoading(true);
      storesAPI
        .getFeedExpand(feedId)
        .then(setProducts)
        .catch((err) => {
          console.error(err);
          setError('Failed to load collection. Please try again.');
          setProducts([]);
        })
        .finally(() => setLoading(false));
    }
  };

  const pageTitle = collectionTitle ?? 'Collection';
  const pageSubtitle = loading
    ? 'Loading products...'
    : error
      ? 'Something went wrong'
      : `${sortedProducts.length} ${sortedProducts.length === 1 ? 'product' : 'products'} in this collection`;

  return (
    <div className={styles.categoryPage}>
      <Header />

      <div className={styles.contentWrapper}>
        <div className={styles.headerRowWithFilters}>
          <div className={styles.pageTitleSection}>
            <h1 className={styles.pageTitle}>{pageTitle}</h1>
            <p className={styles.pageSubtitle}>{pageSubtitle}</p>
          </div>
          <div className={styles.searchHeaderControls}>
            <select
              id="collection-sort"
              className={styles.searchHeaderSort}
              value={sortBy}
              onChange={handleSortChange}
              aria-label="Sort results"
            >
              <option value="newest">Newest First</option>
              <option value="popular">Most Popular</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
            </select>
            <div className={styles.searchHeaderViewToggle} role="group" aria-label="View mode">
              <button
                type="button"
                className={viewMode === 'grid' ? styles.searchHeaderViewBtnActive : styles.searchHeaderViewBtn}
                onClick={() => setViewMode('grid')}
                aria-label="Grid view"
                title="Grid view"
              >
                <i className="fas fa-th-large" aria-hidden />
              </button>
              <button
                type="button"
                className={viewMode === 'list' ? styles.searchHeaderViewBtnActive : styles.searchHeaderViewBtn}
                onClick={() => setViewMode('list')}
                aria-label="List view"
                title="List view"
              >
                <i className="fas fa-list" aria-hidden />
              </button>
            </div>
          </div>
        </div>

        <div className={styles.searchResultsArea}>
          {loading ? (
            <div className={styles.loadingState}>
              <div className={styles.loadingSpinner} aria-hidden />
              <p>Loading products...</p>
            </div>
          ) : error ? (
            <div className={styles.emptyState}>
              <i className="fas fa-exclamation-circle" aria-hidden />
              <h3>{error}</h3>
              <p>We couldn&apos;t load this collection.</p>
              <button type="button" className={styles.retryButton} onClick={handleRetry}>
                Try again
              </button>
            </div>
          ) : sortedProducts.length === 0 ? (
            <div className={styles.emptyState}>
              <i className="fas fa-inbox" aria-hidden />
              <h3>No products in this collection</h3>
              <p>Check back later for new items.</p>
              <Link to="/" className={styles.backLink}>
                Back to Home
              </Link>
            </div>
          ) : (
            <div className={`${styles.productsGrid} ${styles[viewMode]}`}>
              {sortedProducts.map((product, index) => {
                const cardProduct = convertProductToCardFormat(product);
                const productKey = product.id || `product-${index}`;
                return (
                  <Link
                    key={productKey}
                    to={cardProduct.link || '#'}
                    className={styles.productCard}
                  >
                    <div
                      className={`${styles.productImage} ${cardProduct.imageUrl ? styles.hasImage : ''}`}
                      style={{
                        background:
                          cardProduct.imageUrl && !imageErrors.has(productKey)
                            ? 'transparent'
                            : cardProduct.gradient,
                      }}
                    >
                      {cardProduct.discount != null && cardProduct.discount > 0 && (
                        <div className={styles.discountBadge}>
                          {formatDiscountPercentage(cardProduct.discount)}% OFF
                        </div>
                      )}
                      {viewMode === 'grid' && (
                        <button
                          type="button"
                          className={`${styles.wishlistBtn} ${isInWishlist(product.id) ? styles.wishlistBtnActive : ''}`}
                          onClick={(e) => handleWishlistToggle(e, product)}
                          disabled={updatingWishlist.has(product.id || '')}
                          aria-label={isInWishlist(product.id) ? 'Remove from wishlist' : 'Add to wishlist'}
                        >
                          {updatingWishlist.has(product.id || '') ? (
                            <i className="fas fa-spinner fa-spin" aria-hidden />
                          ) : (
                            <i className="fas fa-heart" aria-hidden />
                          )}
                        </button>
                      )}
                      {cardProduct.imageUrl && !imageErrors.has(productKey) ? (
                        <img
                          src={cardProduct.imageUrl}
                          alt={cardProduct.name}
                          className={styles.productImageContent}
                          onError={() => handleImageError(productKey)}
                          onLoad={(e) => {
                            const img = e.target as HTMLImageElement;
                            if (img.naturalWidth === 0 || img.naturalHeight === 0) {
                              handleImageError(productKey);
                            }
                          }}
                        />
                      ) : (
                        <img
                          src={productPlaceholderUrl}
                          alt={cardProduct.name}
                          className={styles.productImageContent}
                        />
                      )}
                    </div>
                    <div className={styles.productInfo}>
                      <div className={styles.productName} title={cardProduct.name}>
                        {cardProduct.name}
                      </div>
                      <div className={styles.productPriceContainer}>
                        {viewMode === 'list' ? (
                          <div className={styles.productPriceGroup}>
                            <span className={styles.productPrice}>{cardProduct.price}</span>
                            {cardProduct.originalPrice && (
                              <span className={styles.originalPrice}>{cardProduct.originalPrice}</span>
                            )}
                          </div>
                        ) : (
                          <>
                            <span className={styles.productPrice}>{cardProduct.price}</span>
                            {cardProduct.originalPrice && (
                              <span className={styles.originalPrice}>{cardProduct.originalPrice}</span>
                            )}
                          </>
                        )}
                        <div className={styles.productPriceActions}>
                          {viewMode === 'list' && (
                            <button
                              type="button"
                              className={`${styles.wishlistBtn} ${isInWishlist(product.id) ? styles.wishlistBtnActive : ''}`}
                              onClick={(e) => handleWishlistToggle(e, product)}
                              disabled={updatingWishlist.has(product.id || '')}
                              aria-label={isInWishlist(product.id) ? 'Remove from wishlist' : 'Add to wishlist'}
                            >
                              {updatingWishlist.has(product.id || '') ? (
                                <i className="fas fa-spinner fa-spin" aria-hidden />
                              ) : (
                                <i className="fas fa-heart" aria-hidden />
                              )}
                            </button>
                          )}
                          <button
                            type="button"
                            className={`${styles.addToCartIcon} ${isInCart(product) ? styles.addToCartIconInCart : ''}`}
                            onClick={(e) => handleAddToCart(product, e)}
                            disabled={addingToCart.has(product.id || '') || !product.id}
                            aria-label={isInCart(product) ? 'In cart' : 'Add to Cart'}
                            title={isInCart(product) ? `In cart (${getCartQuantity(product.id)})` : 'Add to Cart'}
                          >
                            {addingToCart.has(product.id || '') ? (
                              <i className="fas fa-spinner fa-spin" aria-hidden />
                            ) : isInCart(product) ? (
                              <>
                                <i className="fas fa-shopping-cart" aria-hidden />
                                {getCartQuantity(product.id) > 1 && (
                                  <span className={styles.cartQuantityBadge}>{getCartQuantity(product.id)}</span>
                                )}
                              </>
                            ) : (
                              <img
                                src={`${process.env.PUBLIC_URL}/images/icons/add-to-cart.svg`}
                                alt="Add to Cart"
                                width={24}
                                height={24}
                              />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <Footer />
      <ScrollToTop />
    </div>
  );
});

export default CollectionExpandPage;
